import { catalog } from "../i18n.js";

/**
 * The tour's practice script (onboarding.ts): what the practice bot, the
 * Chief of Staff and the Bot Hiring Manager say in it, the suggestions the
 * user can send, the ideas for a first bot, and the first message the user
 * sends the real Bot Hiring Manager.
 */
export const TOUR_SCRIPT = catalog(
  {
    practiceTitle: "Practice bot",
    hello: "Hi! I'm Scout, a practice bot. Give me something to do: pick one below or type your own.",

    saturdayAsk: "Plan a relaxed Saturday in Bandung",
    saturdayAck: "On it. I'll look at what's on and put a day together.",
    saturdayQuery: "relaxed things to do in Bandung on a Saturday",
    saturdayFound: "Lembang in the morning; Sundanese food around Dago; the afternoon angklung show at Saung Angklung Udjo; cafés along Jalan Braga.",
    saturdayRead: "Opening times, and how long the drive between Lembang and the city takes.",
    saturdayAnswer: "Here's an easy one:\n\n- **Morning:** breakfast up in Lembang, while the air is still cool\n- **Lunch:** Sundanese food around Dago, nasi timbel if you like it\n- **Afternoon:** the angklung show at Saung Angklung Udjo\n- **Evening:** coffee and a stroll along Jalan Braga",
    saturdaySubject: "Your Saturday in Bandung",

    invoiceAsk: "Draft a friendly reminder for an unpaid invoice",
    invoiceAck: "Sure. I'll keep it warm but clear.",
    invoicePath: "drafts/invoice-reminder.md",
    invoiceAnswer: (path: string) => `Here's a draft:\n\n> Hi Pak Budi, hope you're well! A quick nudge about invoice #1042 for Rp 4.500.000 from last month, which is still open. Could you let me know when it's likely to be paid? Happy to resend it if that helps. Thank you!\n\nI saved it in your files as \`${path}\` too.`,
    invoiceSubject: "Invoice reminder draft",

    walletsAsk: "Compare GoPay, OVO and DANA for me",
    walletsAck: "Happy to. I'll keep it to what matters day to day.",
    walletsFound: "Feature pages and reviews for GoPay, OVO and DANA.",
    walletsPath: "e-wallets.md",
    walletsAnswer: "Short version:\n\n- **GoPay:** built into Gojek, handy for rides, food and paying in shops\n- **OVO:** works across Grab and many stores, with points on what you spend\n- **DANA:** a wallet of its own, good for transfers and paying bills\n\nAll three pay by QRIS. If you use Gojek most, GoPay; for bills and transfers, DANA.",
    walletsSubject: "GoPay, OVO and DANA, compared",

    ownAck: "On it. I'll dig in and come back to you.",
    ownFound: "The pages a real search would bring back.",
    ownAnswer: (task: string) => `This is a practice run, so I won't really do “${task}”, but a real bot would. It searches, reads pages, works in its own files and terminal, then writes back in short messages, just like this.`,

    chiefExample: "Remind me to pay the PLN bill every month, and find a good dentist near Kemang",
    chiefAck: "Leave it with me.",
    chiefBriefExample: "Please set up a personal assistant for the user: a monthly reminder to pay the PLN bill, and small errands like finding a good dentist near Kemang. Check the details with them and confirm before you create it.",
    chiefBrief: (task: string) => `Please set up a bot for the user to handle this: “${task}”. Check the details with them and confirm before you create it.`,
    managerReply: "Will do. I'll ask them a few questions and confirm with them before making it.",
    chiefDoneExample: (manager: string) => `Nobody on your team does reminders or errands yet, so I've asked the ${manager} to set up an assistant. It will check the details with you first, and once the assistant is there, I'll hand it both jobs.`,
    chiefDone: (manager: string) => `Nobody on your team does that yet, so I've asked the ${manager} to set one up. It will check the details with you first, and once the bot is there, I'll hand it the job.`,

    asideBusy: "Got it, I'll take that into account.",
    asideIdle: "Noted. This is only practice, so I'll leave it there; a real bot would get to work on it.",

    offer: "Want me to email that to you, so it's in your inbox?",
    offerTo: "you",
    offerDetail: (to: string, subject: string) => `To: ${to}\nSubject: ${subject}`,
    offerSummary: "Send an email from your account",
    offerReason: "Sending email on your behalf is something to check with you first.",
    sentResult: "Sent (practice: nothing really left).",
    sent: "Sent. (It's practice, so nothing really went out.)",
    declined: "No problem, I won't send anything.",

    researcherName: "Researcher",
    researcherTitle: "Research",
    researcherLabel: "R",
    researcherPitch: "A researcher who digs into any question on the web and comes back with a short answer and its sources.",
    assistantName: "Assistant",
    assistantTitle: "Personal Assistant",
    assistantLabel: "A",
    assistantPitch: "A personal assistant who keeps track of reminders, bills like PLN and BPJS, follow-ups and the small errands I'd otherwise forget.",
    plannerName: "Trip Planner",
    plannerTitle: "Travel",
    plannerLabel: "T",
    plannerPitch: "A trip planner who works out where to go, how to get there and what to book, with options and prices first.",
    lookoutName: "Lookout",
    lookoutTitle: "Watcher",
    lookoutLabel: "L",
    lookoutPitch: "A lookout who watches pages I care about, like prices on Tokopedia or new kos listings, and tells me the moment something changes.",
    writerName: "Writer",
    writerTitle: "Writing",
    writerLabel: "W",
    writerPitch: "A writer who drafts emails and posts in my voice, warm, plain and short, and shows me every draft before it goes.",

    pitchFrom: (task: string) => `A bot to help with things like “${task}”.`,
    hiringMessage: (job: string) => `Hi! I'm new here and I'd like my first bot. ${job}`,
  },
  {
    practiceTitle: "Bot latihan",
    hello: "Hai! Aku Scout, bot latihan. Kasih aku sesuatu untuk dikerjakan: pilih salah satu di bawah, atau ketik sendiri.",

    saturdayAsk: "Rencanakan Sabtu yang santai di Bandung",
    saturdayAck: "Siap! Aku cek dulu ada apa saja, lalu kususun rencana seharian.",
    saturdayQuery: "tempat santai di Bandung untuk hari Sabtu",
    saturdayFound: "Lembang di pagi hari; kuliner Sunda di sekitar Dago; pertunjukan angklung sore di Saung Angklung Udjo; kafe di sepanjang Jalan Braga.",
    saturdayRead: "Jam buka, dan berapa lama perjalanan dari Lembang ke kota.",
    saturdayAnswer: "Ini rencana yang santai:\n\n- **Pagi:** sarapan di Lembang, mumpung udaranya masih sejuk\n- **Siang:** makan masakan Sunda di sekitar Dago, nasi timbel kalau suka\n- **Sore:** nonton pertunjukan angklung di Saung Angklung Udjo\n- **Malam:** ngopi sambil jalan-jalan di Jalan Braga",
    saturdaySubject: "Rencana Sabtu kamu di Bandung",

    invoiceAsk: "Buatkan pengingat yang ramah untuk tagihan yang belum dibayar",
    invoiceAck: "Oke. Aku buat yang sopan, tapi tetap jelas.",
    invoicePath: "draf/pengingat-tagihan.md",
    invoiceAnswer: (path: string) => `Ini drafnya:\n\n> Halo Pak Budi, semoga sehat selalu! Mau mengingatkan sedikit soal invoice #1042 sebesar Rp 4.500.000 dari bulan lalu, yang sampai sekarang belum dibayar. Boleh dikabari kira-kira kapan bisa dibayarkan? Kalau perlu, invoicenya bisa saya kirim ulang. Terima kasih banyak!\n\nAku juga sudah simpan di file kamu sebagai \`${path}\`.`,
    invoiceSubject: "Draf pengingat tagihan",

    walletsAsk: "Bandingkan GoPay, OVO, dan DANA buat aku",
    walletsAck: "Boleh! Aku fokus ke yang penting buat sehari-hari.",
    walletsFound: "Halaman fitur dan ulasan GoPay, OVO, dan DANA.",
    walletsPath: "dompet-digital.md",
    walletsAnswer: "Versi singkatnya:\n\n- **GoPay:** menyatu dengan Gojek, praktis buat ojek, pesan makanan, dan bayar di toko\n- **OVO:** bisa dipakai di Grab dan banyak toko, dapat poin dari tiap belanja\n- **DANA:** dompet yang berdiri sendiri, cocok buat transfer dan bayar tagihan\n\nKetiganya bisa bayar pakai QRIS. Kalau paling sering pakai Gojek, pilih GoPay; buat tagihan dan transfer, DANA.",
    walletsSubject: "Perbandingan GoPay, OVO, dan DANA",

    ownAck: "Siap! Aku cari tahu dulu, nanti aku kabari.",
    ownFound: "Halaman-halaman yang akan muncul di pencarian sungguhan.",
    ownAnswer: (task: string) => `Ini cuma latihan, jadi aku nggak benar-benar mengerjakan “${task}”, tapi bot sungguhan akan mengerjakannya. Dia mencari, membaca halaman, bekerja di file dan terminalnya sendiri, lalu membalas lewat pesan-pesan singkat, persis seperti ini.`,

    chiefExample: "Ingatkan aku bayar tagihan PLN tiap bulan, dan carikan dokter gigi yang bagus di dekat Kemang",
    chiefAck: "Serahkan saja ke aku.",
    chiefBriefExample: "Tolong siapkan asisten pribadi untuk pengguna ini: pengingat bulanan untuk bayar tagihan PLN, dan urusan kecil seperti mencari dokter gigi yang bagus di dekat Kemang. Cek detailnya dengan dia, dan minta konfirmasinya sebelum kamu membuatnya.",
    chiefBrief: (task: string) => `Tolong siapkan bot untuk pengguna ini yang bisa mengurus hal berikut: “${task}”. Cek detailnya dengan dia, dan minta konfirmasinya sebelum kamu membuatnya.`,
    managerReply: "Siap. Aku akan tanya beberapa hal ke dia dan minta konfirmasinya sebelum membuatnya.",
    chiefDoneExample: (manager: string) => `Belum ada yang mengurus pengingat atau urusan kecil di tim kamu, jadi aku sudah minta ${manager} menyiapkan asisten. Dia akan cek detailnya dengan kamu dulu, dan begitu asistennya siap, dua tugas ini aku serahkan ke asisten itu.`,
    chiefDone: (manager: string) => `Belum ada yang mengerjakan itu di tim kamu, jadi aku sudah minta ${manager} menyiapkan botnya. Dia akan cek detailnya dengan kamu dulu, dan begitu botnya siap, tugas ini aku serahkan ke bot itu.`,

    asideBusy: "Oke, itu aku perhatikan juga.",
    asideIdle: "Noted! Ini cuma latihan, jadi aku berhenti di sini; bot sungguhan akan langsung mengerjakannya.",

    offer: "Mau aku kirim lewat email, biar ada di inbox kamu?",
    offerTo: "kamu",
    offerDetail: (to: string, subject: string) => `Kepada: ${to}\nSubjek: ${subject}`,
    offerSummary: "Kirim email dari akun kamu",
    offerReason: "Mengirim email atas nama kamu perlu dicek ke kamu dulu.",
    sentResult: "Terkirim (latihan: tidak ada yang benar-benar keluar).",
    sent: "Terkirim. (Ini latihan, jadi sebenarnya nggak ada yang keluar.)",
    declined: "Oke, nggak masalah, aku nggak kirim apa-apa.",

    researcherName: "Peneliti",
    researcherTitle: "Riset",
    researcherLabel: "P",
    researcherPitch: "Peneliti yang mencari jawaban atas pertanyaan apa pun di web, lalu kembali dengan jawaban singkat beserta sumbernya.",
    assistantName: "Asisten",
    assistantTitle: "Asisten Pribadi",
    assistantLabel: "A",
    assistantPitch: "Asisten pribadi yang mencatat pengingat, tagihan seperti PLN dan BPJS, hal yang perlu di-follow up, dan urusan kecil yang biasanya aku lupa.",
    plannerName: "Teman Liburan",
    plannerTitle: "Perjalanan",
    plannerLabel: "T",
    plannerPitch: "Perencana liburan yang mencari tujuan, cara ke sana, dan apa yang perlu dipesan, dengan pilihan dan harganya dulu.",
    lookoutName: "Radar",
    lookoutTitle: "Pemantau",
    lookoutLabel: "R",
    lookoutPitch: "Pemantau yang mengawasi halaman yang penting buat aku, seperti harga di Tokopedia atau iklan kos baru, dan langsung kasih tahu begitu ada yang berubah.",
    writerName: "Penulis",
    writerTitle: "Tulisan",
    writerLabel: "P",
    writerPitch: "Penulis yang membuat draf email dan postingan dengan gaya bahasaku, hangat, sederhana, dan singkat, dan menunjukkan setiap draf sebelum dikirim.",

    pitchFrom: (task: string) => `Bot yang membantu hal-hal seperti “${task}”.`,
    hiringMessage: (job: string) => `Halo! Aku baru di sini dan ingin punya bot pertamaku. ${job}`,
  },
);
